import { Layer } from '../core/Layer';
import { ThresholdOptimizer } from './ThresholdOptimizer';

export interface BackgroundRemovalOptions {
  autoTolerance?: boolean;
  manualTolerance?: number;
  contiguous?: boolean;
}

export class BackgroundRemover {
  /**
   * Automatic 1-Click Background Removal with Auto-Threshold Optimization & Foreground Protection
   * Detects backdrop colors from perimeter sampling and removes background with soft edge feathering.
   */
  public static removeBackground(
    layer: Layer,
    options: BackgroundRemovalOptions | number = 38
  ): { removedPixels: number; usedTolerance: number; isAuto: boolean } {
    const width = layer.width;
    const height = layer.height;

    const opt: BackgroundRemovalOptions =
      typeof options === 'number'
        ? { autoTolerance: false, manualTolerance: options, contiguous: true }
        : { autoTolerance: options.autoTolerance !== false, manualTolerance: options.manualTolerance ?? 38, contiguous: options.contiguous !== false };

    // Sample perimeter points around all 4 borders
    const perimeterSeeds: { x: number; y: number }[] = [
      { x: 2, y: 2 },
      { x: width - 3, y: 2 },
      { x: 2, y: height - 3 },
      { x: width - 3, y: height - 3 },
      { x: Math.floor(width / 2), y: 2 },
      { x: 2, y: Math.floor(height / 2) },
      { x: width - 3, y: Math.floor(height / 2) },
      { x: Math.floor(width / 2), y: height - 3 },
      { x: Math.floor(width / 4), y: 2 },
      { x: Math.floor((3 * width) / 4), y: 2 },
      { x: Math.floor(width / 4), y: height - 3 },
      { x: Math.floor((3 * width) / 4), y: height - 3 }
    ];

    const floodResult = ThresholdOptimizer.smartFlood(
      layer.ctx,
      width,
      height,
      perimeterSeeds,
      {
        autoTolerance: opt.autoTolerance,
        manualTolerance: opt.manualTolerance,
        contiguous: opt.contiguous
      }
    );

    const imgData = layer.ctx.getImageData(0, 0, width, height);
    const data = imgData.data;
    const mask = floodResult.mask;

    // Apply alpha erasing and soft boundary feathering
    let removed = 0;
    for (let y = 0; y < height; y++) {
      const row = y * width;
      for (let x = 0; x < width; x++) {
        const idx = row + x;
        if (mask[idx] === 255) {
          // Check if adjacent to an intact foreground pixel
          let isEdge = false;
          if (
            (x > 0 && mask[idx - 1] === 0) ||
            (x < width - 1 && mask[idx + 1] === 0) ||
            (y > 0 && mask[idx - width] === 0) ||
            (y < height - 1 && mask[idx + width] === 0)
          ) {
            isEdge = true;
          }

          const dIdx = idx * 4;
          if (isEdge) {
            data[dIdx + 3] = Math.round(data[dIdx + 3] * 0.18);
          } else {
            data[dIdx + 3] = 0;
          }
          removed++;
        }
      }
    }

    // Second-pass edge artifact purge on the layer data to guarantee zero corner/border remnants
    const purge = ThresholdOptimizer.purgeEdgeArtifacts(data, width, height, undefined, 15);
    removed += purge.purgedPixels;

    layer.ctx.putImageData(imgData, 0, 0);
    return {
      removedPixels: removed,
      usedTolerance: floodResult.usedTolerance,
      isAuto: floodResult.isAuto
    };
  }

  /**
   * Sample-and-Remove Background: Click anywhere to instantly remove that color key and connected backdrop.
   */
  public static removeColorAtPoint(
    layer: Layer,
    clickX: number,
    clickY: number,
    tolerance: number = 32,
    contiguous: boolean = true,
    autoTolerance: boolean = false
  ): { removedPixels: number; usedTolerance: number; isAuto: boolean } {
    const width = layer.width;
    const height = layer.height;
    if (clickX < 0 || clickX >= width || clickY < 0 || clickY >= height) {
      return { removedPixels: 0, usedTolerance: tolerance, isAuto: autoTolerance };
    }

    const floodResult = ThresholdOptimizer.smartFlood(
      layer.ctx,
      width,
      height,
      [{ x: clickX, y: clickY }],
      {
        autoTolerance,
        manualTolerance: tolerance,
        contiguous
      }
    );

    const imgData = layer.ctx.getImageData(0, 0, width, height);
    const data = imgData.data;
    const mask = floodResult.mask;

    let removed = 0;
    for (let i = 0; i < mask.length; i++) {
      if (mask[i] === 255) {
        data[i * 4 + 3] = 0;
        removed++;
      }
    }

    // Clean any lingering edge/corner artifacts
    const purge = ThresholdOptimizer.purgeEdgeArtifacts(data, width, height, undefined, 15);
    removed += purge.purgedPixels;

    layer.ctx.putImageData(imgData, 0, 0);
    return {
      removedPixels: removed,
      usedTolerance: floodResult.usedTolerance,
      isAuto: floodResult.isAuto
    };
  }
}
